import React, { type HTMLAttributes, useRef } from 'react';
import { getFormattedRef } from '@soulhx/fs-common';
import { getPassageViewUrl } from '../../helpers/passage-link-helpers.js';
import { PassageLinkModal } from './PassageLinkModal.js';
import { isStandaloneDisplay, useMousePreview } from '@soulhx/ui-common';
import { PassagePreviewPopover } from './PassagePreviewPopover.js';

export const bibleIcon = '✞';

export type IPassageLink = {
  /**
   * The passage reference (USFM format)
   */
  passage: string;

  /**
   * Version of the Bible to use, if any
   */
  version: string;

  /**
   * Context within this `passage` should be interpreted, if any
   */
  context?: string;

  /**
   * Used by `getFormattedRef` to control whether "unnecessary" verses
   * are displayed in the generated link text
   */
  includeVerses?: boolean;

  /**
   * Hides the link
   */
  disabled?: boolean;

  /**
   * Builds a fully scoped URL (including original origin), not just relative path
   */
  nonRelativeUrl?: boolean;

  /**
   * Serialized Bible viewing options
   */
  options?: string;

  /**
   * Disables the passage preview shown when hovering with a mouse
   */
  noPreview?: boolean;
} & HTMLAttributes<HTMLAnchorElement>;

/**
 * Link that opens a given passage in a new browser window/tab.
 */
export const PassageLink = ({
  passage,
  version,
  context,
  includeVerses = true,
  disabled = false,
  children,
  nonRelativeUrl = false,
  className,
  options = undefined,
  noPreview = false,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  ...props
}: IPassageLink) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { show, targetHandlers, overlayHandlers } = useMousePreview();

  if (disabled) return null;

  if (isStandaloneDisplay()) {
    return (
      <PassageLinkModal
        passage={passage}
        version={version}
        context={context}
        includeVerses={includeVerses}
        className={className}
        options={options}
        noPreview={noPreview}
      >
        {children}
      </PassageLinkModal>
    );
  }

  return (
    <>
      <a
        ref={linkRef}
        href={getPassageViewUrl(passage, version, { context, nonRelativeUrl, options })}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onPointerEnter={(e) => {
          onPointerEnter?.(e);
          if (!noPreview) targetHandlers.onPointerEnter(e);
        }}
        onPointerLeave={(e) => {
          onPointerLeave?.(e);
          targetHandlers.onPointerLeave(e);
        }}
        onPointerDown={(e) => {
          onPointerDown?.(e);
          targetHandlers.onPointerDown();
        }}
        {...props}
      >
        {children ?? getFormattedRef(passage, { includeVerses, context })}
        {bibleIcon}
      </a>

      {!noPreview && (
        <PassagePreviewPopover
          target={linkRef}
          show={show}
          overlayHandlers={overlayHandlers}
          passage={passage}
          version={version}
          context={context}
          includeVerses={includeVerses}
        />
      )}
    </>
  );
};
